import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { StatusBadge } from './StatusBadge';

// Map the raw `commission_reason` stored on payment_transactions plus the
// provider's badge/tier context into a coarse filter bucket that matches the
// CommissionPill variants. Keeping this in one place avoids duplicating the
// variant-resolution logic.
function resolveCommissionBucket(booking) {
  const reason = booking.commission_reason;
  if (!reason && booking.commission == null) return 'none';
  if (reason === 'direct_link') return 'direct';
  if (reason === 'repeat_client') return 'repeat';
  if (reason === 'badge_override') return booking.provider_badge === 'founding' ? 'founding' : 'early';
  if (reason === 'marketplace_new_client') return booking.provider_tier === 'pro' ? 'pro' : 'free';
  return 'unknown';
}

// British-standard "20 Apr 2026 at 14:30" for the date/time column.
function formatAdminDateTime(dateStr, timeStr) {
  if (!dateStr) return '';
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayMonYear = `${d} ${months[m - 1]} ${y}`;
    if (!timeStr) return dayMonYear;
    const t = String(timeStr).slice(0, 5);
    return `${dayMonYear} at ${t}`;
  } catch {
    return `${dateStr} ${timeStr || ''}`.trim();
  }
}

// Small coloured pill for booking attribution source. Accepts canonical
// values and legacy aliases so older rows still render sensibly.
function SourcePill({ source }) {
  const canonical = (() => {
    const v = (source || '').toLowerCase();
    if (v === 'direct' || v === 'direct_link') return 'direct';
    if (v === 'referral' || v === 'referral_link') return 'referral';
    return 'marketplace';
  })();
  const styles = {
    direct:      'bg-emerald-50 text-emerald-700 border-emerald-200',
    referral:    'bg-violet-50 text-violet-700 border-violet-200',
    marketplace: 'bg-stone-50 text-stone-600 border-stone-200',
  }[canonical];
  const label = { direct: 'Direct', referral: 'Referral', marketplace: 'Marketplace' }[canonical];
  return (
    <span
      className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border ${styles}`}
      data-testid={`booking-source-${canonical}`}
      title={source ? `Stored: ${source}` : 'Default: marketplace'}
    >
      {label}
    </span>
  );
}

/**
 * Commission-attribution pill. Translates the stored `commission_reason`
 * plus the provider's badge + tier into a human-readable label, so you can
 * audit commission at a glance without digging into payment_transactions.
 *
 * The backend calculates commission in this priority order:
 *   1. direct_link       → 0% (client arrived via /@username)
 *   2. repeat_client     → 0% (client has booked this provider before)
 *   3. badge_override    → Founding 0% / Early 8%
 *   4. marketplace_new_client → tier default (Free 15%, Pro 8%)
 */
function CommissionPill({ booking }) {
  const reason = booking.commission_reason;
  const rate = booking.commission_rate;
  const badge = booking.provider_badge;
  const tier = booking.provider_tier;

  // No transaction yet (unpaid / abandoned / legacy).
  if (!reason && booking.commission == null) {
    return (
      <span
        className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border bg-stone-50 text-stone-400 border-stone-200"
        data-testid="commission-pill-none"
        title="No paid transaction yet"
      >
       ,
      </span>
    );
  }

  const format = (v) => `${Math.round((v ?? 0) * 100)}%`;
  const ratePct = rate != null ? format(rate) : '';

  const variants = {
    direct_link:         { label: `Direct · 0%`,            tone: 'bg-emerald-50 text-emerald-700 border-emerald-200', testId: 'direct' },
    repeat_client:       { label: `Repeat · 0%`,            tone: 'bg-sky-50 text-sky-700 border-sky-200',             testId: 'repeat' },
    badge_override:      {
      label: badge === 'founding' ? `Founding · 0%` : `Early · ${ratePct || '8%'}`,
      tone:  badge === 'founding' ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-orange-50 text-orange-700 border-orange-200',
      testId: badge === 'founding' ? 'founding' : 'early',
    },
    marketplace_new_client: {
      label: tier === 'pro' ? `Pro · ${ratePct || '8%'}` : `Free · ${ratePct || '15%'}`,
      tone:  tier === 'pro' ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-stone-50 text-stone-600 border-stone-200',
      testId: tier === 'pro' ? 'pro' : 'free',
    },
  };
  const v = variants[reason] || {
    label: ratePct ? `Marketplace · ${ratePct}` : 'Marketplace',
    tone: 'bg-stone-50 text-stone-600 border-stone-200',
    testId: 'unknown',
  };

  const commissionStr = booking.commission != null ? `£${Number(booking.commission).toFixed(2)}` : ',';
  return (
    <span
      className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border ${v.tone}`}
      data-testid={`commission-pill-${v.testId}`}
      title={`Reason: ${reason || 'n/a'} · Commission: ${commissionStr}`}
    >
      {v.label}
    </span>
  );
}

export function AdminBookingsTab({
  filteredBookings, bookingStatusFilter, setBookingStatusFilter,
  bookingProviderFilter, setBookingProviderFilter,
  bookingDateFilter, setBookingDateFilter, uniqueProviderNames,
  bookingSourceFilter, setBookingSourceFilter,
}) {
  const [commissionFilter, setCommissionFilter] = useState('all');

  const visibleBookings = filteredBookings.filter((b) => {
    if (bookingSourceFilter && bookingSourceFilter !== 'all') {
      const raw = (b.booking_source || 'marketplace').toLowerCase();
      const canonical = raw === 'direct_link' ? 'direct'
        : raw === 'marketplace_search' ? 'marketplace'
        : raw;
      if (canonical !== bookingSourceFilter) return false;
    }
    if (commissionFilter !== 'all') {
      if (resolveCommissionBucket(b) !== commissionFilter) return false;
    }
    return true;
  });

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Select value={bookingStatusFilter} onValueChange={setBookingStatusFilter}>
          <SelectTrigger className="w-40 input-zuriya"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={bookingProviderFilter} onValueChange={setBookingProviderFilter}>
          <SelectTrigger className="w-48 input-zuriya"><SelectValue placeholder="Provider" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Providers</SelectItem>
            {uniqueProviderNames.map(p => (
              <SelectItem key={p.provider_id} value={p.provider_id}>{p.brand_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={bookingSourceFilter || 'all'} onValueChange={setBookingSourceFilter} data-testid="admin-booking-source-filter">
          <SelectTrigger className="w-44 input-zuriya"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="marketplace">Marketplace</SelectItem>
            <SelectItem value="direct">Direct</SelectItem>
            <SelectItem value="referral">Referral</SelectItem>
          </SelectContent>
        </Select>
        <Select value={commissionFilter} onValueChange={setCommissionFilter} data-testid="admin-booking-commission-filter">
          <SelectTrigger className="w-48 input-zuriya"><SelectValue placeholder="Commission" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Commissions</SelectItem>
            <SelectItem value="direct">Direct · 0%</SelectItem>
            <SelectItem value="repeat">Repeat · 0%</SelectItem>
            <SelectItem value="founding">Founding · 0%</SelectItem>
            <SelectItem value="early">Early · 8%</SelectItem>
            <SelectItem value="free">Free · 15%</SelectItem>
            <SelectItem value="pro">Pro · 8%</SelectItem>
            <SelectItem value="none">No transaction</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={bookingDateFilter} onChange={e => setBookingDateFilter(e.target.value)} className="input-zuriya w-44" />
        <span className="text-sm text-stone-400">{visibleBookings.length} bookings</span>
      </div>
      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F9F6F0]">
              <tr>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Service</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Client</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Provider</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Date/Time</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Price</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Source</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Commission</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Status</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Payment</th>
              </tr>
            </thead>
            <tbody>
              {visibleBookings.map((b) => (
                <tr key={b.booking_id} className="border-b border-[#E6E4DD] hover:bg-[#F9F6F0]">
                  <td className="py-3 px-4 font-medium text-[#1A1A1A] text-sm">{b.service_name}</td>
                  <td className="py-3 px-4 text-sm text-stone-600">{b.client_name}</td>
                  <td className="py-3 px-4 text-sm text-stone-600">{b.provider_name}</td>
                  <td className="py-3 px-4 text-sm text-stone-500">{formatAdminDateTime(b.date, b.time)}</td>
                  <td className="py-3 px-4 text-sm font-medium text-[#D4AF37]">£{b.price}</td>
                  <td className="py-3 px-4"><SourcePill source={b.booking_source} /></td>
                  <td className="py-3 px-4"><CommissionPill booking={b} /></td>
                  <td className="py-3 px-4"><StatusBadge status={b.status} /></td>
                  <td className="py-3 px-4"><StatusBadge status={b.payment_status || 'unpaid'} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
